import utilService from '../UtilService';

// dictionary (proctorId,liveSessionTimestamp)->peer connection between candidate and proctor.
const P2P_CONNECTIONS = {};
// dictionary (proctorId,liveSessionTimestamp)->array of ice_candidates gathered for this connection.
const LOCAL_ICE_CANDIDATES = {};
const REMOTE_ICE_CANDIDATES = {};
const P2P_WEBCAM_STREAM = {};
const P2P_AUDIO_STREAM = {};
const P2P_OFFER = {};
const P2P_OFFER_PROMISE = {};
const P2P_OWN_ICE_CANDIDATES_PROMISE = {};

// dictionary (proctorId,liveSessionTimestamp)-> timeoutId for the recovery before evicting proctor.
const P2P_RECOVERY_TIMEOUT_ID = {};
// dictionary (proctorId,liveSessionTimestamp)-> Information about the last disconnection.
const P2P_DISCONNECTION = {};

const p2pConnectionStore = {
    getP2PKey(messagerId, liveSessionTimestamp) {
        const p2pKey = `${messagerId}_${liveSessionTimestamp}`;
        return p2pKey;
    },

    addP2PConnection(messagerId, liveSessionTimestamp, connection) {
        const p2pKey = this.getP2PKey(messagerId, liveSessionTimestamp);
        P2P_CONNECTIONS[p2pKey] = connection;
    },

    getP2PConnection(messagerId, liveSessionTimestamp) {
        const p2pKey = this.getP2PKey(messagerId, liveSessionTimestamp);
        return P2P_CONNECTIONS[p2pKey];
    },

    closeP2PConnection(p2pKey) {
        try {
            P2P_CONNECTIONS[p2pKey].close();
        } catch (e) {
            console.log(`Unable to close connection for:${p2pKey}`);
        }
    },

    getLocalIceCandidate(messagerId, liveSessionTimestamp) {
        const p2pKey = this.getP2PKey(messagerId, liveSessionTimestamp);
        return LOCAL_ICE_CANDIDATES[p2pKey];
    },

    addLocalIceCandidate(messagerId, liveSessionTimestamp, iceCandidate) {
        const p2pKey = this.getP2PKey(messagerId, liveSessionTimestamp);
        if (!LOCAL_ICE_CANDIDATES[p2pKey]) {
            LOCAL_ICE_CANDIDATES[p2pKey] = [];
        }

        LOCAL_ICE_CANDIDATES[p2pKey].push(iceCandidate);
    },

    resetLocalIceCandidates(messagerId, liveSessionTimestamp) {
        const p2pKey = this.getP2PKey(messagerId, liveSessionTimestamp);
        LOCAL_ICE_CANDIDATES[p2pKey] = [];
    },

    getRemoteIceCandidates(messagerId, liveSessionTimestamp) {
        const p2pKey = this.getP2PKey(messagerId, liveSessionTimestamp);
        return REMOTE_ICE_CANDIDATES[p2pKey];
    },

    addRemoteIceCandidate(messagerId, liveSessionTimestamp, iceCandidate) {
        const p2pKey = this.getP2PKey(messagerId, liveSessionTimestamp);
        if (!REMOTE_ICE_CANDIDATES[p2pKey]) {
            REMOTE_ICE_CANDIDATES[p2pKey] = [];
        }

        REMOTE_ICE_CANDIDATES[p2pKey].push(iceCandidate);
    },

    resetRemoteIceCandidates(messagerId, liveSessionTimestamp) {
        const p2pKey = this.getP2PKey(messagerId, liveSessionTimestamp);
        REMOTE_ICE_CANDIDATES[p2pKey] = [];
    },

    getRemoteWebcamStream(messagerId, liveSessionTimestamp) {
        const p2pKey = this.getP2PKey(messagerId, liveSessionTimestamp);
        return P2P_WEBCAM_STREAM[p2pKey];
    },

    setRemoteWebcamStream(messagerId, liveSessionTimestamp, webcamStream) {
        const p2pKey = this.getP2PKey(messagerId, liveSessionTimestamp);
        P2P_WEBCAM_STREAM[p2pKey] = webcamStream;
    },

    hasRemoteWebcamStream(messagerId, liveSessionTimestamp) {
        return !!this.getRemoteWebcamStream(messagerId, liveSessionTimestamp);
    },

    getRemoteAudioStream(messagerId, liveSessionTimestamp) {
        const p2pKey = this.getP2PKey(messagerId, liveSessionTimestamp);
        return P2P_AUDIO_STREAM[p2pKey];
    },

    getLatestRemoteAudioStreamByMessagerId(messagerId) {
        const latestMessagerConnectionKey = this.getLatestMessagerConnectionKey(messagerId);
        if (!latestMessagerConnectionKey) {
            return null;
        }
        return P2P_AUDIO_STREAM[latestMessagerConnectionKey];
    },

    setRemoteAudioStream(messagerId, liveSessionTimestamp, audioStream) {
        const p2pKey = this.getP2PKey(messagerId, liveSessionTimestamp);
        P2P_AUDIO_STREAM[p2pKey] = audioStream;
    },

    hasRemoteAudioStream(messagerId, liveSessionTimestamp) {
        return !!this.getRemoteAudioStream(messagerId, liveSessionTimestamp);
    },

    getP2POffer(messagerId, liveSessionTimestamp) {
        const p2pKey = this.getP2PKey(messagerId, liveSessionTimestamp);
        return P2P_OFFER[p2pKey];
    },

    setP2POffer(messagerId, liveSessionTimestamp, offer) {
        const p2pKey = this.getP2PKey(messagerId, liveSessionTimestamp);
        P2P_OFFER[p2pKey] = offer;
    },

    getP2POfferPromise(messagerId, liveSessionTimestamp) {
        const p2pKey = this.getP2PKey(messagerId, liveSessionTimestamp);
        return P2P_OFFER_PROMISE[p2pKey];
    },

    setP2POfferPromise(messagerId, liveSessionTimestamp, promise) {
        const p2pKey = this.getP2PKey(messagerId, liveSessionTimestamp);
        P2P_OFFER_PROMISE[p2pKey] = promise;
    },

    getP2POwnCandidatesPromise(messagerId, liveSessionTimestamp) {
        const p2pKey = this.getP2PKey(messagerId, liveSessionTimestamp);
        return P2P_OWN_ICE_CANDIDATES_PROMISE[p2pKey];
    },

    setP2POwnCandidatesPromise(messagerId, liveSessionTimestamp, promise) {
        const p2pKey = this.getP2PKey(messagerId, liveSessionTimestamp);
        P2P_OWN_ICE_CANDIDATES_PROMISE[p2pKey] = promise;
    },

    getP2PRecoveryTimeoutId(messagerId, liveSessionTimestamp) {
        const p2pKey = this.getP2PKey(messagerId, liveSessionTimestamp);
        return P2P_RECOVERY_TIMEOUT_ID[p2pKey];
    },

    setP2PRecoveryTimeoutId(messagerId, liveSessionTimestamp, recoveryTimeoutId) {
        const p2pKey = this.getP2PKey(messagerId, liveSessionTimestamp);
        P2P_RECOVERY_TIMEOUT_ID[p2pKey] = recoveryTimeoutId;
    },

    getP2PDisconnection(messagerId, liveSessionTimestamp) {
        const p2pKey = this.getP2PKey(messagerId, liveSessionTimestamp);
        return P2P_DISCONNECTION[p2pKey];
    },

    setP2PDisconnection(messagerId, liveSessionTimestamp, disconnection) {
        const p2pKey = this.getP2PKey(messagerId, liveSessionTimestamp);
        P2P_DISCONNECTION[p2pKey] = disconnection;
    },

    resolveAllConnectionMessagerIds() {
        const messagerIds = Object.keys(P2P_CONNECTIONS)
            .map(key => parseInt(key.split('_')[0], 10));
        return [...new Set(messagerIds)];
    },

    resolveP2PKeysByMessagerId(collection, messagerId) {
        return Object.keys(collection).filter(p2pKey => p2pKey.startsWith(`${messagerId}_`));
    },

    parseLiveSessionTimestampFromKey(p2pKey) {
        const timestamp = p2pKey.split('_')?.[1];
        return timestamp ? parseInt(timestamp, 10) : null;
    },

    isLastMessagerConnectionKey(messagerId, liveSessionTimestamp) {
        const p2pKey = this.getP2PKey(messagerId, liveSessionTimestamp);
        return p2pKey === this.getLatestMessagerConnectionKey(messagerId);
    },

    sortP2PKeysByTimestamp(p2pKeys) {
        return p2pKeys.sort((key1, key2) => {
            const liveSessionTimestamp1 = this.parseLiveSessionTimestampFromKey(key1);
            const liveSessionTimestamp2 = this.parseLiveSessionTimestampFromKey(key2);

            if (liveSessionTimestamp1 < liveSessionTimestamp2) {
                return -1;
            }
            if (liveSessionTimestamp1 > liveSessionTimestamp2) {
                return 1;
            }
            return 0;
        });
    },

    getLatestMessagerConnectionKey(messagerId) {
        const p2pConnectionKeys = this.resolveP2PKeysByMessagerId(P2P_CONNECTIONS, messagerId);
        const sortedKeys = this.sortP2PKeysByTimestamp(p2pConnectionKeys);

        return sortedKeys.length ? sortedKeys[sortedKeys.length - 1] : null;
    },

    /**
     * Clears all reconnection recovery timeouts associated to this proctor.
     * @param messagerId The id we use to identify a proctor within a session.
     * @returns {boolean} True when there were entries cleared.
     */
    clearPendingRecoveryWaitingTimeouts(messagerId) {
        const p2pKeysToClean = this.resolveP2PKeysByMessagerId(P2P_RECOVERY_TIMEOUT_ID, messagerId);
        this.clearTimeouts(p2pKeysToClean);

        return !!p2pKeysToClean.length;
    },

    clearTimeouts(p2pKeysToClean) {
        p2pKeysToClean.forEach((p2pKey) => {
            clearTimeout(P2P_RECOVERY_TIMEOUT_ID[p2pKey]);
            delete P2P_RECOVERY_TIMEOUT_ID[p2pKey];
            delete P2P_DISCONNECTION[p2pKey];
        });
    },

    closeP2PConnectionsByMessagerId(messagerId) {
        const p2pKeysToClose = this.resolveP2PKeysByMessagerId(P2P_CONNECTIONS, messagerId);
        p2pKeysToClose.forEach(this.closeP2PConnection);
    },

    closeAllP2PConnections() {
        Object.keys(P2P_CONNECTIONS).forEach(this.closeP2PConnection);
    },

    sanitizeStore() {
        utilService.emptyObject(LOCAL_ICE_CANDIDATES);
        utilService.emptyObject(P2P_AUDIO_STREAM);
        utilService.emptyObject(P2P_CONNECTIONS);
        utilService.emptyObject(P2P_OFFER_PROMISE);
        utilService.emptyObject(P2P_OFFER);
        utilService.emptyObject(P2P_OWN_ICE_CANDIDATES_PROMISE);
        utilService.emptyObject(P2P_WEBCAM_STREAM);
        utilService.emptyObject(REMOTE_ICE_CANDIDATES);
    },
};

export default p2pConnectionStore;
